/**
 * The one way to render a GAU (credit) figure in the billing UI.
 *
 * Before this, every surface picked its own precision: checkpoint leaf rows
 * rendered two decimals while their parent totals, the resource tiles, the
 * chart tooltip and the usage bars all called `Math.round`. Two leaves of 1.4
 * therefore sat under a total of 3, which reads as a broken page rather than as
 * rounding.
 *
 * Credits are recorded at 2dp, so 2dp is the full precision available — this
 * formatter shows exactly the fraction a value has and no more, leaving the
 * (overwhelmingly common) whole numbers looking exactly as they did.
 *
 * Deliberately NOT used for:
 * - chart Y-axis ticks — those are scale marks at intervals Recharts chooses,
 *   not figures anyone reconciles;
 * - pack sizes and purchased quantities (e.g. `credits_purchased_this_cycle`),
 *   which are whole by construction — a fraction there would be a lie;
 * - the *draft* value behind an admin's usage-limit input, which must stay a
 *   bare number the field can parse. The rendered limit does come through
 *   here; it is whole in practice, so it reads identically either way.
 *
 * Known limit: a cluster of sub-0.005 leaves each render `<0.01` under a total
 * that is visibly larger, so "the total foots" holds only at or above 0.005.
 * That is deliberate — the alternative is rendering a real charge as a flat
 * `0`, which reads as a missing number rather than a tiny one.
 */
export function formatGaus(value: number): string {
  // Single chokepoint for every GAU figure on a billing surface, so a bad
  // value has to read as unknown. Without this, NaN prints "NaN", ±Infinity
  // and null slip through the comparison below as a believable "0", and
  // undefined throws.
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  // Float subtraction on remaining balances can land a hair below zero; render
  // that as the zero it is, not "-0".
  if (value <= 0) return "0";
  // A real charge that rounds to "0" next to a nonzero dollar cost looks like a
  // missing number, so say it is small instead of saying it is nothing.
  if (value < 0.005) return "<0.01";
  return value.toLocaleString("en-US", { maximumFractionDigits: 2 });
}
